import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ConfiguratorHeader } from '../../../components/headers';
import { SHOPIFY_PRODUCT_NAME, SHOPIFY_PRODUCT_TAG } from '../../../config';
import { AppStates, ProductChangeEvent } from '../../../types';
import { CartErrors } from '../../../utils/getCartErrors';
import { ContainedButton } from '../../../components/buttons';
import { useRootSelector } from '../../../store';

import { Steps } from '../../accordion/Steps';
import { selectEntity, selectSeeAll } from '../../accordion/accordionSlice';
import { AddToCartButton } from '../../cart/AddToCartButton';
import AllFonts from '../../fonts/AllFonts';
import AllIcons from '../../iconAndLabel/AllIcons';
import { MobilePreviewWrapper } from '../../preview/components/MobilePreviewWrapper';
import AllColors from '../../stylesAndColors/AllColors';
import AllStyles from '../../stylesAndColors/AllStyles';
import { ShowUpsellsButton } from '../../upsells';
import { selectHasUpsells } from '../../upsells/store/upsellsSlice';
import AllLabelVariants from '../../iconAndLabel/AllLabelVariants';
import { selectProductTag } from '../../item/store/itemSlice';
import { ProductPreviewComponent } from '../../preview';
import {
  ConfigSessionSwitcher,
  ConfigureAnotherButton,
  selectIsBelowConfigMinimum,
  selectMinConfigs,
  selectMultiConfigEnabled
} from '../../multiConfig';

interface CustomerConfiguratorProps {
  admin?: boolean;
  productTag?: string;
  embedShopifyVariantId?: string;
  isMobileView: boolean;
  priceCents: number;
  setAppState: Dispatch<SetStateAction<AppStates>>;
  errors?: CartErrors;
  loading: boolean;
  addToCartButton?: boolean;
  cartItemId?: string;
  onProductChange?: (event: ProductChangeEvent) => void;
  isEmbedMode?: boolean;
  onEmbedAddToCart?: (item: {
    variantId: number;
    quantity: number;
    properties: Record<string, string>;
  }) => void;
  multiConfigEnabled?: boolean;
}

function displayErrors(errors?: CartErrors | Record<string, string>) {
  Object.values(errors ?? {}).forEach(value => {
    if (typeof value === 'string') {
      toast.error(value, {
        toastId: value,
        position: 'bottom-right'
      });
    } else {
      displayErrors(value);
    }
  });
}

export function CustomerConfigurator({
  admin = false,
  productTag,
  embedShopifyVariantId,
  isMobileView,
  priceCents,
  setAppState,
  errors,
  loading,
  addToCartButton = true,
  cartItemId,
  onProductChange,
  isEmbedMode = false,
  onEmbedAddToCart,
  multiConfigEnabled = false
}: CustomerConfiguratorProps) {
  const [showErrors, setShowErrors] = useState(false);

  const seeAll = useSelector(selectSeeAll);
  const entity = useSelector(selectEntity);
  const hasUpsells = useSelector(selectHasUpsells);
  // The product-level multiConfigEnabled flag turns on as soon as the
  // product loads, but the session (state.multiConfig) only initializes
  // once item.style etc are set, via an async effect in Configurator.tsx.
  // Gate on both so the button can't be clicked mid-initialization, which
  // would race with that effect and get silently overwritten.
  const multiConfigSessionStarted = useRootSelector(selectMultiConfigEnabled);
  const isBelowConfigMinimum = useRootSelector(selectIsBelowConfigMinimum);
  const minConfigs = useRootSelector(selectMinConfigs);

  // Blocks add-to-cart/checkout while the shopper hasn't yet reached the
  // product's configured minimum number of configurations (e.g. a "Family
  // Starter Labels Pack" that requires at least 2).
  const isReadyForCart =
    !errors && !(multiConfigEnabled && isBelowConfigMinimum);

  useEffect(() => {
    if (isReadyForCart) setShowErrors(false);
  }, [isReadyForCart]);

  const loadedProductTag = useRootSelector(selectProductTag);
  // do not render children until we have a product tag - they will break
  if (!loadedProductTag) {
    return <p>Loading...</p>;
  }
  return (
    <>
      <div>
        <ConfiguratorHeader
          title={SHOPIFY_PRODUCT_NAME}
          priceCents={priceCents}
        />
        {isMobileView && !isEmbedMode && <MobilePreviewWrapper />}
        {isMobileView && isEmbedMode && (
          <div className="px-4 pt-3">
            <ProductPreviewComponent mobile={true} />
          </div>
        )}
        {seeAll && (
          <div
            className="z-10"
            style={{
              backgroundColor: 'var(--nb-body-bg, #ffffff)'
            }}
          >
            {entity == 'Styles' && <AllStyles />}
            {entity == 'Colors' && <AllColors />}
            {entity == 'Fonts' && <AllFonts />}
            {entity == 'Icons' && <AllIcons />}
            {entity == 'LabelVariants' && <AllLabelVariants />}
          </div>
        )}
        <Steps
          admin={admin}
          productTag={productTag || SHOPIFY_PRODUCT_TAG}
          seeAll={seeAll}
          errors={showErrors ? errors : undefined}
        />
      </div>

      {addToCartButton && multiConfigEnabled && multiConfigSessionStarted && (
        <>
          <ConfigSessionSwitcher />
          <ConfigureAnotherButton productTag={productTag} />
        </>
      )}

      {!addToCartButton ? null : loading ? (
        <ContainedButton disabled={true} size="large" className="mt-5 flex-1">
          Loading...
        </ContainedButton>
      ) : !hasUpsells ? (
        <AddToCartButton
          productTag={productTag}
          embedShopifyVariantId={embedShopifyVariantId}
          setAppState={setAppState}
          isReadyForCart={isReadyForCart}
          belowConfigMinimum={multiConfigEnabled && isBelowConfigMinimum}
          cartItemId={cartItemId}
          isEmbedMode={isEmbedMode}
          onEmbedAddToCart={onEmbedAddToCart}
          onClick={() => {
            setShowErrors(true);
            // The generic field-error toast is redundant (and confusing
            // alongside) the multi-config-specific "these names aren't
            // filled in" validation - only the latter should surface when
            // multi-config is enabled. Non-multi-config products are
            // unaffected. Inline error highlighting in <Steps> (driven by
            // showErrors) still applies either way.
            if (!multiConfigEnabled) {
              displayErrors(errors);
            }
          }}
        />
      ) : (
        <ShowUpsellsButton
          setAppState={setAppState}
          isReadyForCart={isReadyForCart}
          belowConfigMinimum={multiConfigEnabled && isBelowConfigMinimum}
          cartItemId={cartItemId}
          productTag={productTag}
          onProductChange={onProductChange}
          onClick={() => {
            setShowErrors(true);
            if (!multiConfigEnabled) {
              displayErrors(errors);
            }
          }}
        />
      )}

      {addToCartButton && multiConfigEnabled && isBelowConfigMinimum && (
        <p className="mt-2 text-center text-sm text-red-500">
          Add at least {minConfigs} names to add to cart.
        </p>
      )}
    </>
  );
}
